// ----- Phase 5.X-publish-governance — Modal #5: Sequence-order warning -----
//
// Shown when this submission's sequence # is NOT the lowest unlocked
// for its NDA — publishing would skip earlier sequences, which the FDA
// gateway will reject. Operator can either reorder first (closes modal,
// navigates Home with the reorder modal pre-opened — depends on Phase
// 5.X-submission-reorder; if not landed, the affordance becomes
// "Cancel — reorder manually first") or publish anyway with an audit
// log entry capturing the override.

import { type ReactElement } from 'react';
import { ModalShell } from './modalShell';
import {
  buttonRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from './modalShellStyles';

export interface SequenceOrderWarningModalProps {
  /** Sequence # of the current submission (e.g. "0003"). */
  readonly currentSequence: string;
  /** Lowest unlocked sequence # for the NDA (e.g. "0001"). */
  readonly lowestUnlockedSequence: string | null;
  /** True when the reorder modal can be invoked (Phase 5.X-submission-reorder landed). */
  readonly reorderAvailable: boolean;
  readonly onPublishAnyway: () => void;
  readonly onReorderFirst: () => void;
  readonly onCancel: () => void;
}

export function SequenceOrderWarningModal({
  currentSequence,
  lowestUnlockedSequence,
  reorderAvailable,
  onPublishAnyway,
  onReorderFirst,
  onCancel,
}: SequenceOrderWarningModalProps): ReactElement {
  return (
    <ModalShell testId="pgov-seqorder-modal" title="Sequence-order warning">
      <p style={{ margin: 0, fontSize: 13 }}>
        You're about to publish sequence{' '}
        <code data-testid="pgov-seqorder-current">{currentSequence}</code>, but
        the lowest unlocked sequence for this application is{' '}
        <code data-testid="pgov-seqorder-lowest">
          {lowestUnlockedSequence ?? '(unknown)'}
        </code>
        . Publishing out of order can cause the FDA gateway to reject the
        submission.
      </p>
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="pgov-seqorder-cancel"
          onClick={onCancel}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pgov-seqorder-reorder"
          onClick={onReorderFirst}
          style={secondaryButtonStyle}
        >
          {reorderAvailable ? 'Reorder first' : 'Cancel — reorder manually first'}
        </button>
        <button
          type="button"
          data-testid="pgov-seqorder-publish-anyway"
          onClick={onPublishAnyway}
          style={primaryButtonStyle}
        >
          Publish anyway
        </button>
      </div>
    </ModalShell>
  );
}
